import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Headphones, Plus, Ticket, Clock, CheckCircle2, AlertCircle, 
  MessageSquare, Send, RefreshCw, ArrowLeft, XCircle
} from "lucide-react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const CATEGORIES = [
  "General Inquiry", "Billing", "Claims", "Policy", "Technical Issue", "Account"
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-800" },
  { value: "medium", label: "Medium", color: "bg-blue-100 text-blue-800" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-800" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-800" }
];

export default function SupportPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Create ticket dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({
    subject: "",
    description: "",
    category: "General Inquiry",
    priority: "medium"
  });
  
  // Reply state
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchTickets();
  }, [navigate]);

  const fetchTickets = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/users/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      } else {
        toast.error("Failed to load tickets");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    setLoadingDetails(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/users/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketDetails(response.data);
      setSelectedTicket(ticketId);
    } catch (error) {
      toast.error("Failed to load ticket details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setCreating(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(`${API}/users/tickets`, newTicket, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Ticket created: ${response.data.ticket_id}`);
      setCreateDialogOpen(false);
      setNewTicket({ subject: "", description: "", category: "General Inquiry", priority: "medium" });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ticket");
    } finally {
      setCreating(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setSending(true);
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/users/tickets/${selectedTicket}/reply`, 
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Reply sent");
      setReplyMessage("");
      fetchTicketDetails(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-yellow-100 text-yellow-800",
      in_progress: "bg-blue-100 text-blue-800",
      resolved: "bg-green-100 text-green-800",
      closed: "bg-gray-100 text-gray-800"
    };
    return styles[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <Clock className="h-4 w-4 text-yellow-600" />,
      in_progress: <RefreshCw className="h-4 w-4 text-blue-600" />,
      resolved: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      closed: <XCircle className="h-4 w-4 text-gray-600" />
    };
    return icons[status] || <Clock className="h-4 w-4" />;
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority);
    return p ? p.color : "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-12">
        <div className="container mx-auto px-4">
          <Button 
            variant="ghost" 
            className="text-primary-foreground mb-4 hover:bg-white/10"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Young Serif, serif' }}>
                <Headphones className="h-8 w-8" />
                Support Center
              </h1>
              <p className="opacity-90 mt-2">Create and track your support tickets</p>
            </div>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-white text-primary hover:bg-white/90"
              data-testid="create-ticket-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Ticket
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {selectedTicket && ticketDetails ? (
          /* Ticket Detail View */
          <div className="max-w-4xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => { setSelectedTicket(null); setTicketDetails(null); }}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Tickets
            </Button>
            
            <Card className="card-organic">
              <CardHeader className="border-b">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                      {ticketDetails.ticket?.subject}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={getStatusBadge(ticketDetails.ticket?.status)}>
                        {ticketDetails.ticket?.status?.replace('_', ' ')}
                      </Badge>
                      <Badge className={getPriorityBadge(ticketDetails.ticket?.priority)}>
                        {ticketDetails.ticket?.priority}
                      </Badge>
                      <Badge variant="outline">{ticketDetails.ticket?.category}</Badge>
                    </div>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>Ticket ID: <code>{ticketDetails.ticket?.ticket_id}</code></p>
                    <p>Created: {new Date(ticketDetails.ticket?.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* Original Description */}
                <div className="bg-muted p-4 rounded-lg mb-6">
                  <p className="text-sm text-muted-foreground mb-2">Original Message:</p>
                  <p className="whitespace-pre-wrap">{ticketDetails.ticket?.description}</p>
                </div>
                
                {/* Replies */}
                <div className="space-y-4 mb-6">
                  {ticketDetails.replies?.map((reply) => (
                    <div 
                      key={reply.reply_id}
                      className={`p-4 rounded-lg ${
                        reply.sender_type === 'agent' 
                          ? 'bg-primary/5 border-l-4 border-primary ml-4' 
                          : 'bg-muted mr-4'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-sm">
                          {reply.sender_type === 'agent' ? (
                            <span className="text-primary">Support Agent</span>
                          ) : (
                            'You'
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(reply.created_at).toLocaleString()}
                        </p>
                      </div>
                      <p className="whitespace-pre-wrap">{reply.message}</p>
                    </div>
                  ))}
                </div>
                
                {/* Reply Form */}
                {ticketDetails.ticket?.status !== 'closed' && (
                  <div className="border-t pt-6">
                    <Label className="mb-2 block">Add a Reply</Label>
                    <Textarea
                      placeholder="Type your message..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      rows={4}
                      className="mb-4"
                      data-testid="reply-textarea"
                    />
                    <Button 
                      onClick={handleSendReply}
                      disabled={sending}
                      data-testid="send-reply-btn"
                    >
                      {sending ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send Reply
                    </Button>
                  </div>
                )}
                
                {ticketDetails.ticket?.status === 'closed' && (
                  <div className="border-t pt-6 text-center text-muted-foreground">
                    <p>This ticket has been closed. If you need further assistance, please create a new ticket.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Ticket List View */
          <div className="max-w-4xl mx-auto">
            <Tabs defaultValue="all">
              <TabsList className="mb-6">
                <TabsTrigger value="all">All Tickets</TabsTrigger>
                <TabsTrigger value="open">Open</TabsTrigger>
                <TabsTrigger value="resolved">Resolved</TabsTrigger>
              </TabsList>
              
              <TabsContent value="all">
                <TicketList 
                  tickets={tickets} 
                  onSelect={fetchTicketDetails}
                  loadingDetails={loadingDetails}
                  getStatusBadge={getStatusBadge}
                  getStatusIcon={getStatusIcon}
                  getPriorityBadge={getPriorityBadge}
                />
              </TabsContent>
              <TabsContent value="open">
                <TicketList 
                  tickets={tickets.filter(t => t.status === 'open' || t.status === 'in_progress')} 
                  onSelect={fetchTicketDetails}
                  loadingDetails={loadingDetails}
                  getStatusBadge={getStatusBadge}
                  getStatusIcon={getStatusIcon}
                  getPriorityBadge={getPriorityBadge}
                />
              </TabsContent>
              <TabsContent value="resolved">
                <TicketList 
                  tickets={tickets.filter(t => t.status === 'resolved' || t.status === 'closed')} 
                  onSelect={fetchTicketDetails}
                  loadingDetails={loadingDetails}
                  getStatusBadge={getStatusBadge}
                  getStatusIcon={getStatusIcon}
                  getPriorityBadge={getPriorityBadge}
                />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
              Create Support Ticket
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input
                placeholder="Brief description of your issue"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                data-testid="ticket-subject"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select 
                  value={newTicket.category} 
                  onValueChange={(val) => setNewTicket({...newTicket, category: val})}
                >
                  <SelectTrigger data-testid="ticket-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={newTicket.priority} 
                  onValueChange={(val) => setNewTicket({...newTicket, priority: val})}
                >
                  <SelectTrigger data-testid="ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                placeholder="Please describe your issue in detail..."
                value={newTicket.description}
                onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                rows={5}
                data-testid="ticket-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={creating} data-testid="submit-ticket-btn">
              {creating ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Ticket className="h-4 w-4 mr-2" />
              )}
              Create Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketList({ tickets, onSelect, loadingDetails, getStatusBadge, getStatusIcon, getPriorityBadge }) {
  if (tickets.length === 0) {
    return (
      <Card className="card-organic">
        <CardContent className="p-12 text-center">
          <Ticket className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No tickets found</p>
          <p className="text-sm text-muted-foreground mt-1">Create a new ticket to get help from our support team</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {tickets.map((ticket) => (
        <Card 
          key={ticket.ticket_id} 
          className="card-organic cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onSelect(ticket.ticket_id)}
          data-testid={`ticket-${ticket.ticket_id}`}
        >
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(ticket.status)}
                  <h3 className="font-semibold">{ticket.subject}</h3>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                  {ticket.description}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={getStatusBadge(ticket.status)}>
                    {ticket.status?.replace('_', ' ')}
                  </Badge>
                  <Badge className={getPriorityBadge(ticket.priority)}>
                    {ticket.priority}
                  </Badge>
                  <Badge variant="outline">{ticket.category}</Badge>
                </div>
              </div>
              <div className="text-right text-sm text-muted-foreground flex-shrink-0">
                <p className="font-mono">{ticket.ticket_id}</p>
                <p>{new Date(ticket.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
